import React, { useEffect, useState } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { useLang } from "../context/LanguageContext";
import { ChevronDown } from "lucide-react";

const HERO_IMG = "/hero-landscape.png";

export default function Hero() {
  const { t } = useLang();
  const { scrollY } = useScroll();
  const y = useTransform(scrollY, [0, 600], [0, 140]);
  const opacity = useTransform(scrollY, [0, 500], [1, 0.2]);

  const [loaded, setLoaded] = useState(false);
  useEffect(() => {
    const img = new window.Image();
    img.src = HERO_IMG;
    img.onload = () => setLoaded(true);
  }, []);

  return (
    <section
      className="relative min-h-[100svh] flex items-center overflow-hidden pt-24 pb-16"
      data-testid="hero-section"
    >
      {/* Background watercolor painting with parallax */}
      <motion.div
        style={{ y, opacity }}
        className="absolute inset-0 -z-10"
      >
        <motion.div
          initial={{ scale: 1.08, opacity: 0 }}
          animate={{ scale: loaded ? 1 : 1.08, opacity: loaded ? 1 : 0 }}
          transition={{ duration: 2.2, ease: [0.22, 1, 0.36, 1] }}
          className="absolute inset-0"
        >
          <img
            src={HERO_IMG}
            alt="The Barn"
            className="w-full h-full object-cover object-center opacity-80 mix-blend-multiply"
          />
        </motion.div>
        {/* warm cream wash */}
        <div className="absolute inset-0 bg-gradient-to-b from-ivory/60 via-ivory/20 to-ivory" />
        {/* burgundy blob */}
        <div
          className="brush-blob bg-burgundy"
          style={{ width: 520, height: 520, top: "-120px", right: "-80px" }}
        />
        <div
          className="brush-blob bg-warm-brown"
          style={{ width: 460, height: 460, bottom: "-120px", left: "-80px" }}
        />
      </motion.div>

      <div className="relative z-10 w-full max-w-7xl mx-auto px-6 md:px-12">
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 0.6 }}
          className="text-xs md:text-sm uppercase tracking-[0.4em] text-burgundy mb-6 md:mb-10"
          data-testid="hero-overline"
        >
          {t.hero.overline}
        </motion.p>

        <div className="grid grid-cols-1 md:grid-cols-[1fr_auto_1fr] items-center gap-2 md:gap-8">
          <motion.h1
            initial={{ opacity: 0, x: -40 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 1.4, delay: 0.8, ease: [0.22, 1, 0.36, 1] }}
            className="font-serif font-light text-5xl sm:text-6xl md:text-7xl lg:text-8xl tracking-tight text-ink md:text-right leading-[1]"
            data-testid="hero-groom-name"
          >
            Natanael
          </motion.h1>

          <motion.span
            initial={{ opacity: 0, scale: 0.6, rotate: -15 }}
            animate={{ opacity: 1, scale: 1, rotate: 0 }}
            transition={{ duration: 1.6, delay: 1.4, ease: [0.22, 1, 0.36, 1] }}
            className="font-script text-6xl md:text-8xl text-burgundy leading-none my-2 md:my-0 text-center"
          >
            &
          </motion.span>

          <motion.h1
            initial={{ opacity: 0, x: 40 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 1.4, delay: 1.1, ease: [0.22, 1, 0.36, 1] }}
            className="font-serif font-light text-5xl sm:text-6xl md:text-7xl lg:text-8xl tracking-tight text-ink md:text-left leading-[1]"
            data-testid="hero-bride-name"
          >
            Raluka
          </motion.h1>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 1.8 }}
          className="mt-10 md:mt-16 flex flex-col items-center gap-3"
        >
          <div className="ornament-rule w-full max-w-md">
            <span className="font-serif italic text-ink/80 text-base sm:text-lg md:text-xl whitespace-nowrap">
              {t.common.date}
            </span>
          </div>
          <p
            className="text-xs md:text-sm uppercase tracking-[0.3em] text-warm-brown"
            data-testid="hero-location"
          >
            {t.hero.location}
          </p>
          <p className="font-serif italic text-base md:text-lg text-ink/75 max-w-xl text-center mt-4 px-4">
            {t.hero.subtitle}
          </p>
        </motion.div>
      </div>

      {/* scroll indicator */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1, delay: 2.4 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 text-ink/60"
      >
        <span className="text-[10px] uppercase tracking-[0.35em]">
          {t.hero.scroll}
        </span>
        <motion.div
          animate={{ y: [0, 8, 0] }}
          transition={{ repeat: Infinity, duration: 2, ease: "easeInOut" }}
        >
          <ChevronDown size={18} />
        </motion.div>
      </motion.div>
    </section>
  );
}
